import { useUserPermissions } from "@dashboard/auth/hooks/useUserPermissions";
import { ColumnPicker } from "@dashboard/components/Datagrid/ColumnPicker/ColumnPicker";
import { useColumns } from "@dashboard/components/Datagrid/ColumnPicker/useColumns";
import Datagrid from "@dashboard/components/Datagrid/Datagrid";
import {
  DatagridChangeStateContext,
  useDatagridChangeState,
} from "@dashboard/components/Datagrid/hooks/useDatagridChange";
import { TablePaginationWithContext } from "@dashboard/components/TablePagination";
import { Vendor, Vendors } from "@dashboard/vendors/types";
import { VendorListUrlSortField } from "@dashboard/vendors/urls";
import { PermissionEnum } from "@dashboard/graphql";
import { getPrevLocationState } from "@dashboard/hooks/useBackLinkWithState";
import { ListProps, SortPage } from "@dashboard/types";
import { Item } from "@glideapps/glide-data-grid";
import { Box } from "@saleor/macaw-ui-next";
import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router";
import {
  createGetCellContent,
  vendorListStaticColumnsAdapter,
} from "../VendorListDataGrid/datagrid";
import { messages } from "../VendorListDataGrid/messages";

interface VendorListDatagridProps extends ListProps, SortPage<VendorListUrlSortField> {
  vendors: Vendors | undefined;
  loading: boolean;
  hasRowHover?: boolean;
  onSelectVendorIds: (rowsIndex: number[], clearSelection: () => void) => void;
  onViewVendor?: (id: string) => void;
}

export const VendorListDatagrid = ({
  vendors,
  sort,
  loading,
  settings,
  onUpdateListSettings,
  hasRowHover,
  disabled,
  onSelectVendorIds,
  onSort,
  onViewVendor,
}: VendorListDatagridProps) => {
  const intl = useIntl();
  const location = useLocation();
  const datagrid = useDatagridChangeState();
  const userPermissions = useUserPermissions();
  const hasManageOrdersPermission =
    userPermissions?.some(perm => perm.code === PermissionEnum.MANAGE_ORDERS) ?? false;
  const vendorListStaticColumns = useMemo(
    () => vendorListStaticColumnsAdapter(intl, sort, hasManageOrdersPermission),
    [intl, sort, hasManageOrdersPermission],
  );
  const onColumnChange = useCallback(
    (picked: string[]) => {
      if (onUpdateListSettings) {
        onUpdateListSettings("columns", picked.filter(Boolean));
      }
    },
    [onUpdateListSettings],
  );
  const { handlers, visibleColumns, staticColumns, selectedColumns, recentlyAddedColumn } =
    useColumns({
      gridName: "vendor_list",
      staticColumns: vendorListStaticColumns,
      selectedColumns: settings?.columns ?? [],
      onSave: onColumnChange,
    });
  const getCellContent = useCallback(
    createGetCellContent({
      vendors,
      columns: visibleColumns,
      onViewVendor,
    }),
    [vendors, visibleColumns, onViewVendor],
  );
  const handleRowClick = useCallback(
    ([_, row]: Item) => {
      // We intentionally do not navigate on row click anymore.
      // This keeps rows non-clickable from a navigation perspective.
      const _rowData: Vendor | undefined = vendors?.[row];
      // No-op: row click is disabled by design.
      void _rowData;
    },
    [vendors],
  );
  const handleHeaderClick = useCallback(
    (col: number) => {
      const columnName = visibleColumns[col].id as VendorListUrlSortField;
      onSort(columnName);
    },
    [visibleColumns, onSort],
  );

  return (
    <DatagridChangeStateContext.Provider value={datagrid}>
      <Datagrid
        readonly
        loading={loading}
        rowMarkers="checkbox-visible"
        columnSelect="single"
        hasRowHover={hasRowHover}
        onColumnMoved={handlers.onMove}
        onColumnResize={handlers.onResize}
        verticalBorder={false}
        rows={vendors?.length ?? 0}
        availableColumns={visibleColumns}
        emptyText={intl.formatMessage(messages.empty)}
        onRowSelectionChange={onSelectVendorIds}
        getCellContent={getCellContent}
        getCellError={() => false}
        selectionActions={() => null}
        menuItems={() => []}
        // Note: onRowClick exists but does not navigate now; it is effectively disabled.
        onRowClick={handleRowClick}
        onHeaderClicked={handleHeaderClick}
        // We do NOT pass rowAnchor anymore, so hovering will NOT produce a navigation link.
        rowAnchor={undefined}
        recentlyAddedColumn={recentlyAddedColumn}
        renderColumnPicker={() => (
          <ColumnPicker
            staticColumns={staticColumns}
            selectedColumns={selectedColumns}
            onToggle={handlers.onToggle}
          />
        )}
        navigatorOpts={{ state: getPrevLocationState(location) }}
      />
      <Box paddingX={6}>
        <TablePaginationWithContext
          component="div"
          settings={settings}
          disabled={disabled}
          onUpdateListSettings={onUpdateListSettings}
        />
      </Box>
    </DatagridChangeStateContext.Provider>
  );
};
